import { FC, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { Box, Flex, HStack } from '@chakra-ui/react';
import { isNil } from 'ramda';

import { CompanyInvitationRole } from '__generated__/GQL';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Card from 'components/_core/Card';
import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import Column from 'components/Column';
import { isValidEmail } from 'components/LegacyInput/utils';
import Row from 'components/Row';
import BooleanRadioGroup from 'features/Onboarding/components/BooleanRadioGroup';
import EditPersonalNameFields from 'features/Onboarding/legacy/components/EditPersonalNameFields';
import { Gender } from 'features/Onboarding/legacy/types';

import getRoleLabel from '../../getRoleLabel';
import { FormSchema } from '../../index';
import { ManageElement } from '../../styles';
import ManageShares from '../ManageShares';
import locales from './locales';
import {
  EditedBeneficiary,
  ManageBeneficiaryElementComponentProps,
  SavedBeneficiary,
} from './types';

const getTypeSafeGender = (
  gender: string | undefined | null,
): Gender | null => {
  if (gender === 'female' || gender === 'male') {
    return gender;
  }
  return null;
};

const createEditedBeneficiaryFromProps = ({
  beneficiary,
  includeInvitationRole,
}: ManageBeneficiaryElementComponentProps): EditedBeneficiary => {
  return {
    firstName: beneficiary?.firstName ?? '',
    gender: getTypeSafeGender(beneficiary?.gender),
    inviteeEmail: beneficiary?.invitation?.inviteeEmail ?? '',
    isDirector: beneficiary?.isDirector ?? null,
    // If we don’t want to includeInvitationRole, the mutation will
    // fail if we pass a defined isFutureAccountHolder
    isFutureAccountHolder: includeInvitationRole
      ? beneficiary?.invitation?.role === CompanyInvitationRole.AccountHolder
      : undefined,
    isMajorShareholder: beneficiary?.isMajorShareholder ?? null,
    lastName: beneficiary?.lastName ?? '',
    numberOfShares: beneficiary?.numberOfShares ?? null,
  };
};

const ManageBeneficiaryElementComponent: FC<
  ManageBeneficiaryElementComponentProps
> = (props) => {
  const {
    beneficiary,
    disableAccountHolderSwitch,
    getIsAddedAmountSharesValid,
    includeInvitationRole,
    includeShares,
    isDeleting,
    isEditing,
    isExistingCompany,
    isSaving,
    onCardClicked,
    onClose,
    onDelete,
    onSave,
    sharesData,
  } = props;
  const tense = isExistingCompany ? 'present' : 'future';
  const isLoading = isDeleting || isSaving;
  const { clearErrors } = useFormContext<FormSchema>();
  const { errors } = useFormState<FormSchema>();

  const [editedBeneficiary, setEditedBeneficiary] = useState<EditedBeneficiary>(
    createEditedBeneficiaryFromProps(props),
  );

  const [hasShares, setHasShares] = useState<boolean | null>(
    isNil(beneficiary?.numberOfShares)
      ? null
      : Boolean(editedBeneficiary.numberOfShares),
  );

  const isGlobalAmountValid =
    !includeShares ||
    getIsAddedAmountSharesValid(
      editedBeneficiary.numberOfShares,
      beneficiary?.companyLegalBeneficiaryId,
    );

  const hasSetEligibility = includeShares
    ? !isNil(hasShares) && (!hasShares || editedBeneficiary.numberOfShares)
    : editedBeneficiary.isMajorShareholder !== null;

  const isFormInvalid =
    !editedBeneficiary.firstName ||
    !editedBeneficiary.lastName ||
    !editedBeneficiary.gender ||
    !isValidEmail(editedBeneficiary.inviteeEmail) ||
    editedBeneficiary.isDirector === null ||
    !hasSetEligibility;

  const isValidBeneficiary =
    editedBeneficiary.isDirector === true ||
    (includeShares ? hasShares : editedBeneficiary.isMajorShareholder === true);

  const percentOfShares =
    sharesData?.legalNumberOfShares && editedBeneficiary.numberOfShares
      ? (editedBeneficiary.numberOfShares / sharesData?.legalNumberOfShares) *
        100
      : 0;

  const isMajorShareholder = includeShares
    ? percentOfShares >= 25
    : editedBeneficiary.isMajorShareholder;

  const canBeSaved = !isFormInvalid && isGlobalAmountValid;

  return (
    <ManageElement.Wrapper
      onValidSubmit={() => {
        if (canBeSaved && !isLoading) {
          // isDirector and isMajorShareholder can't be null thanks to previous check
          onSave({
            ...editedBeneficiary,
            isMajorShareholder,
          } as SavedBeneficiary);
        }
      }}
    >
      <Card cursor={!isEditing ? 'pointer' : 'default'} onClick={onCardClicked}>
        <Flex alignItems="stretch" direction="column">
          {!isEditing ? (
            <Row alignItems="center" justifyContent="space-between">
              <Column>
                <Text fontWeight="weight-500" variant="primary">
                  {`${beneficiary?.firstName} ${beneficiary?.lastName}`}
                </Text>

                {isFormInvalid ? (
                  <ManageElement.MissingInfoContainer>
                    {locales.missingInfo}
                  </ManageElement.MissingInfoContainer>
                ) : (
                  <ManageElement.RoleContainer>
                    {getRoleLabel({
                      isDirector: editedBeneficiary.isDirector,
                      isMajorShareholder,
                      percentOfShares,
                    })}
                  </ManageElement.RoleContainer>
                )}

                {beneficiary?.invitation?.role ===
                CompanyInvitationRole.AccountHolder ? (
                  <Text fontWeight="weight-500" marginTop="space-4" size="sm">
                    <SunshineIcon marginInlineEnd="space-4" name="star" />
                    {locales.accountHolder}
                  </Text>
                ) : null}

                <Button
                  alignSelf="start"
                  marginTop="space-12"
                  variant="inline-primary"
                >
                  {locales.modify}
                </Button>
              </Column>
            </Row>
          ) : (
            <>
              <ManageElement.CardHeader>
                <Text fontWeight="weight-500" variant="primary">
                  {locales.edit}
                </Text>
                <IconButton
                  aria-label="close"
                  icon="cross"
                  onClick={() => {
                    setEditedBeneficiary(
                      createEditedBeneficiaryFromProps(props),
                    );
                    onClose();
                  }}
                  size="sm"
                  variant="tertiary"
                />
              </ManageElement.CardHeader>
              <EditPersonalNameFields
                firstName={editedBeneficiary.firstName}
                gender={editedBeneficiary.gender}
                lastName={editedBeneficiary.lastName}
                onFirstNameChange={(newFirstName) => {
                  setEditedBeneficiary({
                    ...editedBeneficiary,
                    firstName: newFirstName,
                  });
                }}
                onGenderChange={(newGender) => {
                  setEditedBeneficiary({
                    ...editedBeneficiary,
                    gender: newGender,
                  });
                }}
                onLastNameChange={(newLastName) => {
                  setEditedBeneficiary({
                    ...editedBeneficiary,
                    lastName: newLastName,
                  });
                }}
              />
              <Form.Field label={locales.inviteeEmail} marginY="space-24">
                <Form.Input
                  onChange={(event) => {
                    setEditedBeneficiary({
                      ...editedBeneficiary,
                      inviteeEmail: event.target.value,
                    });
                  }}
                  required
                  type="email"
                  value={editedBeneficiary.inviteeEmail}
                />
              </Form.Field>

              <ManageElement.BusinessRoleLabel>
                {locales.directorLabel[tense]}
              </ManageElement.BusinessRoleLabel>
              <ManageElement.BusinessRoleDescription>
                {locales.directorDescription[tense]}
              </ManageElement.BusinessRoleDescription>
              <Form.Field marginBottom="space-24">
                <BooleanRadioGroup
                  onChange={(value) => {
                    setEditedBeneficiary({
                      ...editedBeneficiary,
                      isDirector: value,
                    });
                  }}
                  value={editedBeneficiary.isDirector}
                />
              </Form.Field>

              {includeShares ? (
                <>
                  <ManageElement.BusinessRoleLabel>
                    {locales.shareholderLabel[tense]}
                  </ManageElement.BusinessRoleLabel>
                  <ManageElement.BusinessRoleDescription>
                    {locales.shareholderDescription}
                  </ManageElement.BusinessRoleDescription>
                  <Form.Field marginBottom="space-24">
                    <BooleanRadioGroup
                      onChange={(value) => {
                        setHasShares(value);

                        if (!value) {
                          setEditedBeneficiary({
                            ...editedBeneficiary,
                            numberOfShares: 0,
                          });
                        } else {
                          setEditedBeneficiary({
                            ...editedBeneficiary,
                            numberOfShares: null,
                          });
                        }
                      }}
                      value={hasShares}
                    />
                  </Form.Field>
                  {hasShares ? (
                    <Box marginBottom="space-24">
                      <ManageShares
                        individualNumOfShares={
                          // convert 0 as null so it's not shown in the input if we switch from non-shareholder to shareholder
                          editedBeneficiary.numberOfShares || null
                        }
                        isGlobalAmountValid={isGlobalAmountValid}
                        onSharesChange={(value) => {
                          setEditedBeneficiary({
                            ...editedBeneficiary,
                            numberOfShares: value,
                          });
                        }}
                        sharesData={sharesData}
                      />
                    </Box>
                  ) : null}
                </>
              ) : (
                <>
                  <ManageElement.BusinessRoleLabel>
                    {locales.majorShareholderLabel[tense]}
                  </ManageElement.BusinessRoleLabel>
                  <ManageElement.BusinessRoleDescription>
                    {locales.majorShareholderDescription[tense]}
                  </ManageElement.BusinessRoleDescription>
                  <Form.Field marginBottom="space-24">
                    <BooleanRadioGroup
                      onChange={(value) =>
                        setEditedBeneficiary({
                          ...editedBeneficiary,
                          isMajorShareholder: value,
                        })
                      }
                      value={editedBeneficiary.isMajorShareholder}
                    />
                  </Form.Field>
                </>
              )}
              {!hasSetEligibility || isValidBeneficiary ? null : (
                <Callout marginBottom="space-24" variant="error">
                  <Callout.Content>
                    {includeShares
                      ? locales.invalidWithSharesMessage
                      : locales.invalidMessage}
                  </Callout.Content>
                </Callout>
              )}
              {includeInvitationRole ? (
                <Form.Field
                  error={errors.hasAccountHolder?.message}
                  hint={
                    disableAccountHolderSwitch
                      ? locales.accountHolderHint
                      : undefined
                  }
                  marginBottom="space-24"
                >
                  <HStack>
                    <Text
                      fontWeight="weight-500"
                      marginInlineEnd="space-16"
                      variant="primary"
                    >
                      {locales.accountHolderLabel}
                    </Text>
                    <Form.SunshineSwitch
                      id={`isAccountHolder-${beneficiary?.companyLegalBeneficiaryId}`}
                      isChecked={
                        editedBeneficiary.isFutureAccountHolder || false
                      }
                      isDisabled={disableAccountHolderSwitch}
                      onChange={(event) => {
                        setEditedBeneficiary({
                          ...editedBeneficiary,
                          isFutureAccountHolder: event.target.checked,
                        });
                        clearErrors('hasAccountHolder');
                      }}
                    />
                  </HStack>
                </Form.Field>
              ) : null}
            </>
          )}
        </Flex>
        {isEditing ? (
          <HStack
            alignItems="center"
            borderTopWidth={1}
            justifyContent="space-between"
            marginTop="space-8"
            paddingTop="space-16"
          >
            <Button
              icon="trash"
              iconPosition="left"
              isDisabled={isLoading}
              isLoading={isDeleting}
              onClick={onDelete}
              variant="danger"
            >
              {locales.delete}
            </Button>
            <Button
              icon="tick"
              iconPosition="left"
              isDisabled={isLoading || !canBeSaved}
              isLoading={isSaving}
              type="submit"
            >
              {locales.save}
            </Button>
          </HStack>
        ) : null}
      </Card>
    </ManageElement.Wrapper>
  );
};

export default ManageBeneficiaryElementComponent;
